import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ToastrService } from '@core/services/toastr.service';
import { UnsubscribeService } from '@core/services/unsubscribe.service';
import {
  Column,
  GridDataSource,
  GridQuery,
  NOTIFICATION_STATUS_OPTIONS,
  NOTIFICATION_TYPE_OPTIONS,
  NotificationListItem,
  NotificationSaveConfig,
  NotificationStatus,
  NotificationType,
  SortDirection
} from '@shared/models';
import { of } from 'rxjs';
import { catchError, concatMap, filter, mapTo, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ModalService } from '../../modules/modal/modal.service';
import {
  EditAlertAndNotificationModalData,
  EditNotificationModalComponent
} from './edit-notification-modal/edit-notification-modal.component';
import {
  AlertAndNotificationModalData,
  NotificationModalComponent
} from './notification-modal/notification-modal.component';
import { AdminNotificationsApiService } from './notifications.service';
import { MemberFirmService } from '@core/services/member-firm.service';

@Component({
  selector: 'gcc-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  providers: [UnsubscribeService]
})
export class NotificationsComponent implements OnInit {
  @ViewChild('country', { static: true }) public countryTemplate: TemplateRef<any>;
  @ViewChild('actions', { static: true }) public actionsTemplate: TemplateRef<any>;
  @ViewChild('publish', { static: true }) public publishTemplate: TemplateRef<any>;

  public dataSource: AlertsAndNotificationsDataSource;
  public notificationStatus = NotificationStatus;

  constructor(
    private notificationsApi: AdminNotificationsApiService,
    private toastr: ToastrService,
    private modalService: ModalService,
    private memberFirmService: MemberFirmService,
    private unsubscribe$: UnsubscribeService
  ) {}

  public ngOnInit(): void {
    this.dataSource = new AlertsAndNotificationsDataSource(
      this.notificationsApi,
      this.memberFirmService,
      this.actionsTemplate,
      this.countryTemplate,
      this.publishTemplate
    );
  }

  public handleDeleteItem({ notificationId, type, title }: NotificationListItem): void {
    this.modalService
      .confirm$({
        title: 'notifications.titles.delete',
        message: ['common.messages.delete_confirmation.description', 'common.messages.delete_confirmation.question']
      })
      .pipe(switchMap(() => this.notificationsApi.delete(notificationId)))
      .subscribe(() => {
        this.toastr.success('notifications.messages.delete', 2000, { type: NotificationType[type], title });
        this.dataSource.reload();
      });
  }

  public handleCopyItem(item: NotificationListItem): void {
    const { title, endDate, countryCode, briefDescription, description, type } = item;
    const copy = {
      title: `Copy of ${title}`,
      endDate,
      countryCode,
      briefDescription,
      description,
      type
    } as NotificationListItem;

    this.handleAddItem(copy);
  }

  public handleEditPublishedItem(item: NotificationListItem): void {
    const { title, endDate, countryCode, briefDescription, description, type, notificationId, publishDate } = item;
    const editableNotification = {
      notificationId,
      title,
      endDate,
      countryCode,
      briefDescription,
      description,
      type,
      publishDate
    } as NotificationListItem;
    this.openNotificationModal(editableNotification, { isEditMode: true })
      .pipe(
        switchMap(([notificationConfig, modalRef]) => {
          return this.notificationsApi
            .update(notificationConfig.notification.notificationId, notificationConfig.notification)
            .pipe(
              mapTo([notificationConfig, modalRef]),
              catchError(() => of(null)),
              filter(Boolean)
            );
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([notificationConfig, modalRef]) => {
        modalRef.hide();
        this.dataSource.reload();
        this.toastr.success('notifications.messages.update', 2000, {
          type: NotificationType[notificationConfig.notification.type],
          title
        });
        this.toastr.success('notifications.messages.add', 2000, {
          type: NotificationType[notificationConfig.notification.type],
          title: notificationConfig.notification.title
        });
      });
  }
  public handlePublishItem({ notificationId, type, title, endDate, status }: NotificationListItem): void {
    if (status === NotificationStatus.Draft && endDate < new Date()) {
      this.toastr.error('notifications.messages.end_date_in_past', 2000);
      return;
    }

    this.modalService
      .confirm$({
        title: 'notifications.titles.publish',
        message: [
          'notifications.messages.publish_confirmation.question',
          'notifications.messages.publish_confirmation.description'
        ]
      })
      .pipe(switchMap(() => this.notificationsApi.updateStatus(notificationId, NotificationStatus.Published)))
      .subscribe(() => {
        this.toastr.success('notifications.messages.publish', 2000, { type: NotificationType[type], title });
        this.dataSource.reload();
      });
  }

  public handleArchiveItem({ notificationId, type, title }: NotificationListItem): void {
    this.modalService
      .confirm$({
        title: 'notifications.titles.archive',
        message: [
          'notifications.messages.archive_confirmation.question',
          'notifications.messages.archive_confirmation.description'
        ]
      })
      .pipe(switchMap(() => this.notificationsApi.updateStatus(notificationId, NotificationStatus.Archived)))
      .subscribe(() => {
        this.toastr.success('notifications.messages.archive', 2000, { type: NotificationType[type], title });

        this.dataSource.reload();
      });
  }

  public handleEditNotification(item: NotificationListItem): void {
    item.status === NotificationStatus.Draft ? this.editDraftNotification(item) : this.handleEditPublishedItem(item);
  }

  public handleAddItem(copy?: NotificationListItem): void {
    let createdNotification: NotificationListItem;

    this.openNotificationModal(copy)
      .pipe(
        switchMap(([notificationConfig, modalRef]) => {
          return this.notificationsApi.create(notificationConfig.notification).pipe(
            tap((_notification: NotificationListItem) => (createdNotification = _notification[0])),
            mapTo([notificationConfig, modalRef]),
            catchError(() => of(null)),
            filter(Boolean)
          );
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([notificationConfig, modalRef]) => {
        modalRef.hide();
        if (notificationConfig.isPublish) {
          this.notificationsApi
            .updateStatus(createdNotification.notificationId, NotificationStatus.Published)
            .subscribe(() => {
              this.dataSource.reload();
              this.toastr.success('notifications.messages.publish', 2000, {
                type: NotificationType[notificationConfig.notification.type],
                title: notificationConfig.notification.title
              });
            });
        } else {
          this.dataSource.reload();
        }
        this.toastr.success(copy ? 'notifications.messages.copy' : 'notifications.messages.add', 2000, {
          type: NotificationType[notificationConfig.notification.type],
          title: notificationConfig.notification.title
        });
      });
  }
  public editDraftNotification(item?: NotificationListItem): void {
    this.memberFirmService.memberFirms$
      .pipe(
        concatMap(countries =>
          this.modalService.openModal<AlertAndNotificationModalData, NotificationSaveConfig>(
            NotificationModalComponent,
            {
              componentData: {
                notification: item,
                countries,
                isEditMode: true
              },
              modalOptions: { class: 'min-width-1000' }
            },
            false
          )
        ),
        switchMap(([notificationConfig, modalRef]) =>
          this.notificationsApi.update(item.notificationId, notificationConfig.notification).pipe(
            mapTo([notificationConfig, modalRef]),
            catchError(() => of(null)),
            filter(Boolean)
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([notificationConfig, modalRef]) => {
        // @ts-ignore
        modalRef.hide();
        if ((notificationConfig as NotificationSaveConfig).isPublish) {
          this.notificationsApi.updateStatus(item.notificationId, NotificationStatus.Published).subscribe(() => {
            this.dataSource.reload();
            this.toastr.success('notifications.messages.publish', 2000, {
              type: NotificationType[(notificationConfig as NotificationSaveConfig).notification.type],
              title: (notificationConfig as NotificationSaveConfig).notification.title
            });
          });
        } else {
          this.dataSource.reload();
        }
        this.toastr.success('notifications.messages.update', 2000, {
          type: NotificationType[(notificationConfig as NotificationSaveConfig).notification.type],
          title: (notificationConfig as NotificationSaveConfig).notification.title
        });
      });
  }

  public changeEndDate(item: NotificationListItem): void {
    this.modalService
      .openModal<EditAlertAndNotificationModalData, Date>(
        EditNotificationModalComponent,
        {
          componentData: { notification: item },
          modalOptions: { class: 'min-width-1000' }
        },
        false
      )
      .pipe(
        switchMap(([endDate, modalRef]) =>
          this.notificationsApi.updateEndDate(item.notificationId, endDate).pipe(
            mapTo([item, modalRef]),
            catchError(() => of(null))
          )
        ),
        filter(Boolean)
      )
      .subscribe(([notification, modalRef]) => {
        // @ts-ignore
        modalRef.hide();
        this.dataSource.reload();
        this.toastr.success('notifications.messages.update', 2000, {
          type: NotificationType[(notification as NotificationListItem).type],
          title: (notification as NotificationListItem).title
        });
      });
  }

  public onPublishClick(e: MouseEvent, publishItem: NotificationListItem): void {
    e.preventDefault();
    this.handlePublishItem(publishItem);
  }

  private openNotificationModal(
    notification?: NotificationListItem,
    mode: { isEditMode?: boolean } = { isEditMode: false }
  ): any {
    const { isEditMode } = mode;
    return this.memberFirmService.memberFirms$.pipe(
      concatMap(countries =>
        this.modalService.openModal<AlertAndNotificationModalData, NotificationSaveConfig>(
          NotificationModalComponent,
          {
            componentData: {
              isEditMode,
              notification,
              countries
            },
            modalOptions: { class: 'min-width-1000' }
          },
          false
        )
      )
    );
  }
}

class AlertsAndNotificationsDataSource implements GridDataSource<NotificationListItem> {
  public columns: Column.GridColumn[];
  public defaultParameters: GridQuery;

  public reload: () => void;

  constructor(
    private api: AdminNotificationsApiService,
    private memberFirmService: MemberFirmService,
    actionsTemplate: TemplateRef<any>,
    countryTemplate: TemplateRef<any>,
    publishTemplate: TemplateRef<any>
  ) {
    this.columns = [
      new Column.EnumColumn('status', 'Status', NotificationStatus)
        .addMapClass((item: NotificationListItem) => NotificationStatus[item.status])
        .addFiltration(
          new Column.ColumnFilter(Column.ColumnFilterType.Options, {
            options: NOTIFICATION_STATUS_OPTIONS,
            allowSearch: true
          })
        ),
      new Column.EnumColumn('type', 'Type', NotificationType).addFiltration(
        new Column.ColumnFilter(Column.ColumnFilterType.Options, {
          options: NOTIFICATION_TYPE_OPTIONS,
          allowSearch: true
        })
      ),
      new Column.StringColumn('title', 'Title'),
      new Column.CustomTemplateColumn('countryCode', 'Member firm', countryTemplate).addFiltration(
        new Column.ColumnFilter(Column.ColumnFilterType.Options, { options: [], allowSearch: true })
      ),
      new Column.CustomTemplateColumn('publishDate', 'Published on', publishTemplate)
        .addSorting({ [SortDirection.Ascending]: 'Ascending', [SortDirection.Descending]: 'Descending' })
        .addFiltration(new Column.ColumnFilter(Column.ColumnFilterType.Daterange, {})),
      new Column.DateColumn('endDate', 'End date')
        .addSorting({ [SortDirection.Ascending]: 'Ascending', [SortDirection.Descending]: 'Descending' })
        .addFiltration(new Column.ColumnFilter(Column.ColumnFilterType.Daterange, {})),
      new Column.DateColumn('lastUpdatedAt', 'Updated on').addSorting(),
      new Column.StringColumn('lastUpdatedByName', 'Updated by'),
      new Column.CustomTemplateColumn('action', '', actionsTemplate).setAlignToRight()
    ];

    this.memberFirmService.memberFirms$.subscribe(data => {
      (this.columns.find(c => c.name === 'countryCode').filter.config as Column.ColumnOptionsFilter).options = data.map(
        i => ({
          text: i.name,
          value: i.countryCode
        })
      );
    });

    this.defaultParameters = new GridQuery(1, 10, 'lastUpdatedAt', true);
  }

  public query(params: GridQuery) {
    return this.api.getList(params);
  }
}
